import React, { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel
} from '@mui/material';
import { Add, Edit, Delete, Close } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { IClientFloors, IClientPlaces, ITables, TABLE_STATUS } from '../../all-types';
import { RestaurantTable } from '../../components'; // Make sure this path is correct
import { addRestaurantTable, deleteRestaurantTable, updateRestaurantTable } from '../../services';

const TablesScreen: React.FC = () => {
    const dispatcher = useAppDispatch();
    const places: IClientPlaces[] = useAppSelector(state => state.preloadData.places);
    const floors: IClientFloors[] = useAppSelector(state => state.preloadData.floors);
    const tables: ITables[] = useAppSelector(state => state.preloadData.tables);

    const [openDialog, setOpenDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const initialFormState: ITables = {
        tableName: '',
        tableNo: 0,
        capacity: 0,
        isActive: false,
        floorId: '',
        floorNo: 0,
        xAxis: 0,
        yAxis: 0,
        placeId: '',
        place: '',
        width: 0,
        height: 0,
        rotation: 0,
        status: TABLE_STATUS.FREE,
        tableIcon: '',
        tableImage: '',
        tableColor: "black",
        chairColor: "black",
        tableType: "DEFAULT",
        billSession: JSON.parse('{}')
    };

    const [form, setForm] = useState<ITables>(initialFormState);
    const [tableToDelete, setTableToDelete] = useState<ITables | null>(null);

    const handleChange = (key: keyof ITables, value: any) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const handleOpenCreate = () => {
        setForm({
            ...initialFormState,
            // Keep these specific defaults if needed
            status: TABLE_STATUS.FREE,
            billSession: JSON.parse('{}')
        });
        setIsEditMode(false);
        setOpenDialog(true);
    };

    const handleOpenEdit = (table: ITables) => {
        setForm(table);
        setIsEditMode(true);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setIsEditMode(false);
    };

    const handleSave = () => {
        try {
            if (isEditMode && form?._id) {
                dispatcher(updateRestaurantTable({ id: form._id, table: form }));
            } else {
                dispatcher(addRestaurantTable(form));
            }
        } catch (error) {
            console.error("Error saving table:", error);
        } finally {
            handleCloseDialog();
        }
    };

    const confirmDelete = (table: ITables) => {
        setTableToDelete(table);
        setDeleteDialogOpen(true);
    };

    const handleDelete = () => {
        try {
            if (tableToDelete?._id) {
                dispatcher(deleteRestaurantTable(tableToDelete._id));
                setDeleteDialogOpen(false);
            }
        } catch (e) {
            console.error("Error deleting table:", e);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" mb={3}>
                <Typography variant="h4">Tables Management</Typography>
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenCreate}>
                    Add Table
                </Button>
            </Stack>

            {/* Table Tiles */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
                {tables.map(table => (
                    <Paper key={table.tableName} elevation={3} sx={{ p: 2 }}>
                        <RestaurantTable isFree={table.status === TABLE_STATUS.FREE} table={table} />
                        <Stack direction="row" justifyContent="center" mt={1}>
                            <IconButton
                                onClick={() => handleOpenEdit(table)}
                                aria-label={`Edit table ${table.tableName}`}
                            >
                                <Edit color="primary" />
                            </IconButton>
                            <IconButton
                                onClick={() => confirmDelete(table)}
                                aria-label={`Delete table ${table.tableName}`}
                            >
                                <Delete color="error" />
                            </IconButton>
                        </Stack>
                    </Paper>
                ))}
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>
                    {isEditMode ? 'Edit Table' : 'Add New Table'}
                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Table Name"
                                value={form.tableName}
                                onChange={(e) => handleChange('tableName', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Table No"
                                type="number"
                                value={form.tableNo}
                                onChange={(e) => handleChange('tableNo', Number(e.target.value))}
                                required
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                value={form.floorId}
                                onChange={(e, v) => handleChange('floorId', v || '')}
                                options={floors.map(f => f._id || '')}
                                getOptionLabel={id => {
                                    const floor = floors.find(f => f._id === id);
                                    return floor ? `Floor ${floor.floorNo}` : id;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Floor"
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                value={form.placeId}
                                onChange={(e, v) => handleChange('placeId', v || '')}
                                options={places.map(p => p._id || '')}
                                getOptionLabel={id => {
                                    const place = places.find(p => p._id === id);
                                    return place ? place.placeName : id;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Place"
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Capacity"
                                type="number"
                                value={form.capacity}
                                onChange={(e) => handleChange('capacity', Number(e.target.value))}
                                required
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} display="flex" alignItems="center">
                            <Checkbox
                                checked={form.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                            />
                            <Typography>Is Active</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel component="legend">Table Type</FormLabel>
                            <RadioGroup
                                row
                                value={form.tableType}
                                onChange={(e) => handleChange('tableType', e.target.value)}
                            >
                                <FormControlLabel value="CIRCULAR" control={<Radio />} label="Circular" />
                                <FormControlLabel value="RECTANGULAR" control={<Radio />} label="Rectangular" />
                                <FormControlLabel value="SQUARE" control={<Radio />} label="Square" />
                                <FormControlLabel value="DEFAULT" control={<Radio />} label="Default" />
                            </RadioGroup>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormLabel>Table Color</FormLabel>
                            <TextField
                                fullWidth
                                type="color"
                                value={form.tableColor}
                                onChange={(e) => handleChange('tableColor', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel>Chair Color</FormLabel>
                            <TextField
                                fullWidth
                                type="color"
                                value={form.chairColor}
                                onChange={(e) => handleChange('chairColor', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                    </Grid>

                    <Box mt={3}>
                        <RestaurantTable isFree={true} table={form} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {isEditMode ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete table <strong>{tableToDelete?.tableName}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export { TablesScreen };